import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogTitle-root": {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: theme.spacing(2),
  },
}));

const countVars = (text) => {
  const matches = (text || "").match(/\{\{\s*\d+\s*\}\}/g) || [];
  const nums = matches.map((m) => parseInt(m.replace(/\D/g, ""), 10));
  return nums.length ? Math.max(...nums) : 0;
};

const bodyOf = (template) => {
  const comps = template?.components || [];
  const body = comps.find((c) => c.type === "BODY");
  return body?.text || "";
};

/**
 * Atalho de "janela fechada": escolhe um template aprovado, preenche as
 * variáveis e envia para o contato do ticket (reabre a conversa).
 */
const SendTemplateFromTicketModal = ({ open, onClose, ticket }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [params, setParams] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchApproved = async () => {
      try {
        const { data } = await api.get("/metaTemplates", {
          params: { status: "APPROVED" },
        });
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchApproved();
    setSelectedId("");
    setParams([]);
  }, [open]);

  const selected = useMemo(
    () => templates.find((t) => String(t.id) === String(selectedId)),
    [templates, selectedId]
  );

  const bodyText = bodyOf(selected);
  const varCount = countVars(bodyText);

  useEffect(() => {
    setParams(Array(varCount).fill(""));
  }, [selectedId, varCount]);

  const previewBody = useMemo(() => {
    let txt = bodyText;
    params.forEach((p, i) => {
      txt = txt.replace(
        new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, "g"),
        p || `{{${i + 1}}}`
      );
    });
    return txt;
  }, [bodyText, params]);

  const handleSend = async () => {
    if (!selected) {
      toast.error("Escolha um template aprovado.");
      return;
    }
    setSending(true);
    try {
      await api.post(`/tickets/${ticket.id}/send-template`, {
        templateId: selected.id,
        bodyParameters: params,
      });
      toast.success("Template enviado ao cliente.");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  if (!ticket) return null;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar template (reabrir conversa)</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="textSecondary">
          A janela de 24h está fechada. Envie um template aprovado para
          retomar a conversa com {ticket?.contact?.name || "o cliente"}.
        </Typography>

        <TextField
          select
          label="Template aprovado"
          fullWidth
          size="small"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          sx={{ mt: 2 }}
        >
          {templates.length === 0 && (
            <MenuItem value="" disabled>
              Nenhum template aprovado disponível
            </MenuItem>
          )}
          {templates.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name} ({t.language})
            </MenuItem>
          ))}
        </TextField>

        {selected && varCount > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
              Variáveis
            </Typography>
            <Grid container spacing={1}>
              {params.map((p, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <TextField
                    label={`Variável {{${i + 1}}}`}
                    fullWidth
                    size="small"
                    value={p}
                    onChange={(e) => {
                      const next = [...params];
                      next[i] = e.target.value;
                      setParams(next);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {selected && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#075e54" : "#dcf8c6",
              whiteSpace: "pre-wrap",
              fontSize: "0.875rem",
            }}
          >
            {previewBody || "(sem corpo)"}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={sending}>
          Cancelar
        </Button>
        <ButtonWithSpinner
          loading={sending}
          disabled={!selected}
          onClick={handleSend}
          color="primary"
          variant="contained"
        >
          Enviar
        </ButtonWithSpinner>
      </DialogActions>
    </StyledDialog>
  );
};

export default SendTemplateFromTicketModal;
